// Sync drainer — walks the `pending_writes` queue when the browser
// comes back online and replays each queued RPC against the server.
//
// Replay strategy:
//   • On success: drop the row from the queue.
//   • On *hard* failure (server returned ok:false or 4xx/5xx with a
//     non-zero status): drop the row too. The server explicitly
//     rejected; retrying won't help. We log a warning so the user
//     can see the rejection in dev tools.
//   • On *network* failure (RpcError.status === 0): keep the row;
//     next online cycle tries again.
//
// The drainer fires on:
//   1. App boot (catches anything queued in a previous session)
//   2. `window.online` event (browser reports network restored)
//   3. A 60s safety poll (some networks lie about `online` events;
//      polling catches the cases where we silently came back up).
//
// Bypasses the cached `rpc()` wrapper deliberately — replaying
// through the wrapper would re-queue the same writes we're trying
// to drain, infinite loop. We import the raw transport via a
// dedicated export from rpc.ts.

import { localStore, type PendingWrite } from "./local-store";
import { rpcRaw, RpcError } from "./rpc";
import { lookupMutator } from "./cache-mutators";

const POLL_INTERVAL_MS = 60 * 1000;
let _started = false;
let _draining = false;

// Lifecycle events the offline indicator subscribes to. PENDING_EVENT
// (from local-store) handles "queue grew/shrank"; these add the
// in-flight transitions so the pill can flip to "Syncing…" while a
// drain is actually doing work.
export const SYNC_DRAINING_EVENT = "hub:sync-draining";
export const SYNC_DRAINED_EVENT  = "hub:sync-drained";

function emit(name: string, detail?: Record<string, unknown>) {
  try { window.dispatchEvent(new CustomEvent(name, { detail })); } catch {}
}

export function startSyncDrainer() {
  if (_started) return;
  _started = true;

  // Boot-time drain — handles writes queued in a previous session.
  setTimeout(() => drain().catch(() => {}), 1500);

  window.addEventListener("online", () => {
    console.info("[sync] online event — draining");
    drain().catch(() => {});
  });

  // Safety net for environments that don't fire `online` reliably
  // (mobile webviews on intermittent cell sometimes miss it).
  window.setInterval(() => {
    if (navigator.onLine === false) return;
    drain().catch(() => {});
  }, POLL_INTERVAL_MS);
}

async function drain(): Promise<void> {
  // Single in-flight drain at a time — reentrant calls just no-op.
  if (_draining) return;
  _draining = true;
  let succeeded = 0;
  let failed = 0;
  try {
    const queue = await localStore.listPending().catch(() => [] as PendingWrite[]);
    if (queue.length === 0) return;
    console.info(`[sync] draining ${queue.length} queued write(s)`);
    emit(SYNC_DRAINING_EVENT, { total: queue.length });
    for (const entry of queue as DrainPendingWrite[]) {
      if (entry.id === undefined) continue;
      const ok = await replay(entry);
      if (ok) {
        await localStore.deletePending(entry.id).catch(() => {});
        succeeded++;
      } else {
        failed++;
        if (entry.lastErrIsNetwork) {
          // Network died again — stop draining; the rest would all
          // fail the same way and burn through their attempt counters.
          // Next online cycle picks up where we left off.
          break;
        }
      }
    }
    // Tell any UI watching the queue (the offline indicator in
    // Phase D) that it just shrank.
    try { window.dispatchEvent(new CustomEvent(localStore.PENDING_EVENT)); } catch {}
  } finally {
    _draining = false;
    emit(SYNC_DRAINED_EVENT, { succeeded, failed });
  }
}

export function isDraining(): boolean { return _draining; }

// PendingWrite with a transient flag set during replay so drain()
// can distinguish "network died again" from "server rejected" and
// decide whether to keep going through the rest of the queue.
interface DrainPendingWrite extends PendingWrite {
  lastErrIsNetwork?: boolean;
}

async function replay(entry: DrainPendingWrite): Promise<boolean> {
  if (entry.id === undefined) return false;
  try {
    // Use rpcRaw so the response payload (incl. {ok:false, error:...})
    // is visible and we can decide whether the server actually
    // accepted the write. Bypasses the cache wrapper which would
    // re-queue this very call.
    const resp = await rpcRaw<{ ok?: boolean; error?: string }>(entry.method, entry.args);
    if (resp && resp.ok === false) {
      // Server explicitly rejected (e.g. note conflict, validation
      // error). Don't retry — keep dropping the row but log it.
      console.warn(`[sync] ${entry.method} rejected by server:`, resp.error);
      return true; // treat as "drained" so the row is removed
    }
    // Apply the real server response to the read caches so the
    // queued temp-id / `_queued: true` placeholder gets replaced
    // with the authoritative item. This clears the "not synced
    // yet" badge in the UI on the next render.
    const mutator = lookupMutator(entry.method);
    if (mutator) {
      await mutator(entry.args, resp as unknown as Record<string, any>)
        .catch((e) => console.warn(`[sync] mutate ${entry.method} after replay failed:`, e?.message || e));
    }
    return true;
  } catch (err: any) {
    const isNetwork = err instanceof RpcError && err.status === 0;
    entry.lastErrIsNetwork = isNetwork;
    if (isNetwork) {
      // Still offline — bump attempts, leave for next cycle.
      await localStore.markPendingAttempt(entry.id, err).catch(() => {});
      return false;
    }
    // Other error (401, 5xx, etc.) — the server is reachable but
    // unhappy. Drop the row so it doesn't poison the queue.
    console.warn(`[sync] ${entry.method} hard-failed (${err?.message || err}); dropping`);
    return true;
  }
}
